import { academicYearDisplayRange } from "./displayRange";

const THAI_YEAR_OFFSET = 543;

function beToCe(beYear) {
  return String(Number(beYear) - THAI_YEAR_OFFSET);
}

function ceToBe(ceYear) {
  return String(Number(ceYear) + THAI_YEAR_OFFSET);
}

function buildAcademicYears() {
  const { latestBeYear, earliestBeYear } = academicYearDisplayRange;
  const years = [];

  for (let beYear = latestBeYear; beYear >= earliestBeYear; beYear -= 1) {
    years.push({
      ce: beToCe(beYear),
      be: String(beYear),
      label: `พ.ศ. ${beYear}`,
      folder: String(beYear),
    });
  }

  return years;
}

export const academicYears = buildAcademicYears();
export const defaultAcademicYear = academicYears[0];

const contentContext = require.context("./", true, /^\.\/\d{4}\/content\.js$/);

const yearContentByBeYear = contentContext.keys().reduce((contentByYear, key) => {
  const folder = key.split("/")[1];
  const module = contentContext(key);

  return {
    ...contentByYear,
    [folder]: module.default || module,
  };
}, {});

export const yearContentByCeYear = Object.fromEntries(
  Object.entries(yearContentByBeYear).map(([beYear, content]) => [
    beToCe(beYear),
    content,
  ])
);

const beToCeYear = Object.fromEntries(
  academicYears.map(({ be, ce }) => [be, ce])
);
const supportedCeYears = academicYears.map((item) => item.ce);
const defaultCeYear = defaultAcademicYear?.ce || "2026";
const defaultContent =
  academicYears.map(({ be }) => yearContentByBeYear[be]).find(Boolean) ||
  yearContentByCeYear[defaultCeYear] ||
  Object.values(yearContentByBeYear)[0];

function safeLocalStorageYear() {
  if (typeof window === "undefined") return defaultCeYear;
  return window.localStorage.getItem("year") || defaultCeYear;
}

export function resolveAcademicYear(input) {
  const raw = String(input || safeLocalStorageYear());
  if (beToCeYear[raw]) return beToCeYear[raw];
  if (supportedCeYears.includes(raw)) return raw;
  return defaultCeYear;
}

export function resolveAcademicBeYear(input) {
  return ceToBe(resolveAcademicYear(input));
}

export function hasAcademicYearContent(input) {
  const ceYear = resolveAcademicYear(input);
  const beYear = ceToBe(ceYear);
  return Boolean(yearContentByBeYear[beYear] || yearContentByCeYear[ceYear]);
}

function clone(value) {
  return value == null ? value : JSON.parse(JSON.stringify(value));
}

function publicUrl() {
  return (process.env.PUBLIC_URL || "").replace(/\/$/, "");
}

function hydratePublicUrl(value) {
  if (Array.isArray(value)) return value.map(hydratePublicUrl);
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [key, hydratePublicUrl(item)])
    );
  }
  if (typeof value === "string") {
    return value.replaceAll("__PUBLIC_URL__", publicUrl());
  }
  return value;
}

export function notFoundPayload() {
  return { code: 404, status: "Not found", data: [] };
}

export function getYearContent(year) {
  const ceYear = resolveAcademicYear(year);
  const beYear = ceToBe(ceYear);
  return hydratePublicUrl(
    clone(yearContentByBeYear[beYear] || yearContentByCeYear[ceYear] || defaultContent)
  );
}

export function getPagePayload(year, pageKey) {
  return getYearContent(year).pages?.[pageKey] || notFoundPayload();
}

export function getTableGpaPayload(year, group) {
  const tableGpa = getYearContent(year).table_gpa || {};
  return group
    ? tableGpa[group] || tableGpa.all || notFoundPayload()
    : tableGpa.all || notFoundPayload();
}

export function getInstitutePayload(year, course) {
  return getYearContent(year).institutes?.[course] || notFoundPayload();
}

export function getBranchPayload(year, branch) {
  return getYearContent(year).branches?.[branch] || notFoundPayload();
}

export function getBranchContractPayload(year, course) {
  return getYearContent(year).branch_contract?.[course] || notFoundPayload();
}
